/**
 * 薄抽象层：让 SPA 同时跑 PWA 和 Tauri。在浏览器里所有 desktop API 都是 no-op，
 * 不会 import 任何 @tauri-apps/* 代码（动态 import 切走 chunk）。
 *
 * 检测策略：Tauri 2 在 webview 注入 `__TAURI_INTERNALS__`；PWA 里没有。
 */

const ALWAYS_ON_TOP_KEY = 'doneline.desktop.alwaysOnTop';

export function isDesktop(): boolean {
  return typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window;
}

/**
 * PWA service worker 只在真浏览器注册。两个条件缺一不可：
 * - http(s) 协议：macOS Tauri 是 tauri:// 协议，registerSW 直接抛错；
 * - 非桌面壳：Windows/Linux Tauri 的 webview 用 http(s) 形态协议，**协议启发式挡不住**——
 *   SW 一旦在壳内注册会劫持资源缓存/更新流，桌面端更新语义归 Tauri updater 管。
 */
export function shouldRegisterServiceWorker(protocol: string): boolean {
  return /^https?:$/.test(protocol) && !isDesktop();
}

/**
 * 监听 Rust 侧 emit 的 quick-capture 事件（global-shortcut 触发）。
 * 返回 unlisten — 浏览器场景返回 noop。
 */
export async function onQuickCapture(cb: () => void): Promise<() => void> {
  if (!isDesktop()) return () => {};
  const { listen } = await import('@tauri-apps/api/event');
  return listen('desktop:quick-capture', () => cb());
}

/**
 * 切主窗口 always-on-top。浏览器场景直接 no-op。
 */
export async function setAlwaysOnTop(on: boolean): Promise<void> {
  if (!isDesktop()) return;
  const { invoke } = await import('@tauri-apps/api/core');
  await invoke('set_always_on_top', { on });
}

export function readAlwaysOnTopPref(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(ALWAYS_ON_TOP_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeAlwaysOnTopPref(on: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    if (on) window.localStorage.setItem(ALWAYS_ON_TOP_KEY, '1');
    else window.localStorage.removeItem(ALWAYS_ON_TOP_KEY);
  } catch {
    /* quota / private mode — 静默 */
  }
}
